import { localCatalog } from '../data/catalog'
import { localSite } from '../data/site'
import { pages as localPages } from '../data/pages'
import { clone } from './editing'
import { brandKit, designs } from './marketing/marketingSeed'
import { socialIdeas, socialPosts } from './social/socialSeed'
import { newsletters } from './newsletter/newsletterSeed'
import type { Catalog } from '../core/types/catalog'
import type { SiteSettings } from '../core/types/site'
import type { PageContent } from '../core/types/pages'
import type { Marketing } from '../core/types/marketing'

/**
 * Het concept van het beheer zonder React: de vorm, de beginstand en het vergelijken met de
 * opgeslagen stand ("gewijzigd"). Puur en los te testen; `AdminContext` houdt alleen de state.
 */

export type ListKey = 'products' | 'routines' | 'treatments' | 'articles' | 'concerns'
export type ListItem<K extends ListKey> = Catalog[K][number]

/**
 * De vlakken die apart "gewijzigd" kunnen zijn. Marketing is één blok naar de server, maar vier
 * vlakken hier (ontwerpen, merkstijl, social, nieuwsbrief), zodat het stipje in het menu bij het juiste scherm staat.
 */
export type AdminArea = ListKey | 'site' | 'pages' | 'designs' | 'brandKit' | 'social' | 'newsletters'

export type Draft = { catalog: Catalog; site: SiteSettings; pages: PageContent; marketing: Marketing }

/** Behandelingen en artikelen heten naar hun slug; de rest heeft een id. */
const bySlug: Record<ListKey, boolean> = {
  products: false,
  routines: false,
  treatments: true,
  articles: true,
  concerns: false,
}

export const itemId = (key: ListKey, item: ListItem<ListKey>): string => {
  if (bySlug[key]) return 'slug' in item ? item.slug : ''
  return 'id' in item ? item.id : ''
}

/** De vlakken die "gewijzigd" kunnen zijn, in vaste volgorde. */
export const areas: readonly AdminArea[] = ['products', 'routines', 'treatments', 'articles', 'concerns', 'site', 'pages', 'designs', 'brandKit', 'social', 'newsletters']

export const initialDraft = (): Draft => ({
  catalog: clone(localCatalog),
  site: clone(localSite()),
  pages: clone(localPages),
  marketing: clone({ designs, brandKit, posts: socialPosts, ideas: socialIdeas, newsletters }),
})

/**
 * Elk vlak wordt alleen opnieuw tekst als het zelf veranderde: de editors werken onveranderlijk,
 * dus een ongewijzigd vlak houdt dezelfde verwijzing. Zo kost een toetsaanslag in één product niet
 * elf keer `JSON.stringify` van de hele catalogus, site en marketing.
 */
const plainJson = new WeakMap<object, string>()
const newsletterJson = new WeakMap<object, string>()
const cached = (cache: WeakMap<object, string>, value: object, make: () => string): string => {
  const hit = cache.get(value)
  if (hit !== undefined) return hit
  const text = make()
  cache.set(value, text)
  return text
}
const json = (value: object) => cached(plainJson, value, () => JSON.stringify(value))

export const snapshot = (d: Draft): Record<AdminArea, string> => ({
  products: json(d.catalog.products),
  routines: json(d.catalog.routines),
  treatments: json(d.catalog.treatments),
  articles: json(d.catalog.articles),
  concerns: json(d.catalog.concerns),
  site: json(d.site),
  pages: json(d.pages),
  designs: json(d.marketing.designs),
  brandKit: json(d.marketing.brandKit),
  social: `${json(d.marketing.posts)}|${json(d.marketing.ideas)}`,
  // `delivery` is van de server (zoals voorraad): telt nooit als wijziging.
  newsletters: cached(newsletterJson, d.marketing.newsletters, () => JSON.stringify(d.marketing.newsletters.map(({ delivery: _d, ...n }) => n))),
})

/** Niets te herstellen (het item bestond al niet meer). Eén vaste functie, geen lege arrowfunctie. */
export function noUndo(): void {
  return undefined
}
